import { useEffect, useState } from 'react'
import { useProgress } from '@react-three/drei'

import { productsReadyStore, useStore } from '../../store/uiStore'
import FacciaMascotte from './FacciaMascotte'

// ============================================================
// Schermata di caricamento "risveglio": due palpebre chiudono lo
// schermo mentre si carica (respirando piano, come nel sonno);
// quando tutto è pronto sbattono un paio di volte — spiraglio,
// richiusura, spiraglio più ampio — e infine si spalancano,
// con la vista che torna a fuoco (velo di blur che svanisce).
// Pronto = asset 3D (useProgress) + prodotti Shopify
// (productsReadyStore); il caricamento arriva a ondate, quindi
// si chiude solo dopo QUIET_MS di quiete.
// ============================================================

const QUIET_MS = 600
const WAKE_MS = 4000 // durata del risveglio (allineata alle animazioni CSS)

// ------------------------------------------------------------
// LA MASCOTTE
// Una palla morbida coi colori del personaggio della favicon, sul
// modello dell'avatar di bloub (github.com/jeremy-prt/bloub).
//
// IL CORPO STA FERMO, ED È VOLUTO. Le prime versioni lo facevano
// ondeggiare deformando la sagoma; nel loro codice c'è scritto,
// misurato sul video di riferimento, che a riposo la palla è un
// CERCHIO: raggio costante, centro stabile, respiro di mezzo punto
// percentuale sulla sola altezza. Tutta la vita passa dallo sguardo e
// dai battiti di ciglia. Deformare il contorno la faceva sembrare
// gelatina, non una creatura.
//
// IL CARICAMENTO È IL LORO STATO "thinking": la palla si rimpicciolisce
// fino a DIVENTARE il puntino di mezzo di tre, che pulsano a turno.
// Misure prese dal loro catalogo (src/bot/decor.ts e states.ts):
// i tre punti stanno a -0.557, -0.013 e +0.532 raggi dal centro, hanno
// raggio 0.165 e gonfiano fino a 1.25; il battito dura 1.5 s ed è
// sfalsato di mezzo secondo per punto. Il punto di mezzo NON e' un
// oggetto a parte: e' la palla stessa, raccolta alla sua misura — cosi' non
// c'e' nessun passaggio di consegne che possa lampeggiare.
//
// CORPO E PUNTINI SONO DIV, NON CERCHI SVG. Si muovono solo con
// trasformazioni e opacita', e su un elemento HTML quelle le porta avanti
// il compositore anche mentre il thread principale e' bloccato a
// decodificare modelli e texture — MISURATO: 341 ms di fila proprio mentre
// la palla si raccoglie. Dentro un <svg> invece si fermano e poi saltano:
// erano gli scatti segnalati. Tutto il perche' e i tempi in style.css.
// ------------------------------------------------------------

export default function LoadingScreen() {
  const { active } = useProgress()
  const productsReady = useStore(productsReadyStore)
  const [awake, setAwake] = useState(false) // sbattito di palpebre avviato
  const [gone, setGone] = useState(false)   // rimossa dal DOM

  useEffect(() => {
    if (awake || !productsReady || active) return
    const t = setTimeout(() => setAwake(true), QUIET_MS)
    return () => clearTimeout(t)
  }, [productsReady, active, awake])

  useEffect(() => {
    if (!awake) return
    const t = setTimeout(() => setGone(true), WAKE_MS)
    return () => clearTimeout(t)
  }, [awake])

  if (gone) return null

  return (
    <div className={awake ? 'loading-screen awake' : 'loading-screen'}>
      {/* vista annebbiata che torna a fuoco durante il risveglio */}
      <div className="loading-veil" />
      {/* palpebre */}
      <div className="loading-lid top" />
      <div className="loading-lid bottom" />
      {/* testo e barra, sopra le palpebre finché si dorme */}
      <div className="loading-content">
        <div className="loading-mascotte" aria-hidden="true">
          {/* Le due scritte corrono su archi. Stanno in un <svg> fermo sotto a
              tutto: si muovono solo col contenitore (deriva), insieme alla
              mascotte. */}
          <svg viewBox="0 0 220 220" className="mascotte-scritte">
            <defs>
              <path id="arco-titolo" d="M 11 110 A 99 99 0 0 1 209 110" />
              {/* L'arco di sotto ha il verso OPPOSTO (flag di sweep 0):
                  percorso da sinistra a destra passando sotto, le
                  lettere restano dritte e la curva sorride. Con lo
                  stesso verso di quello sopra il testo uscirebbe
                  capovolto. Raggio piu' largo perche' qui i caratteri
                  crescono VERSO la palla, non lontano da lei. */}
              <path id="arco-sotto" d="M -12 110 A 122 122 0 0 0 232 110" />
            </defs>
            <text className="mascotte-titolo">
              <textPath href="#arco-titolo" startOffset="50%">MOMOZ SHOP</textPath>
            </text>
            <text className="mascotte-sottotitolo">
              <textPath href="#arco-sotto" startOffset="50%">Stiamo aprendo il negozio…</textPath>
            </text>
          </svg>
          {/* i due puntini laterali PRIMA della palla, cioe' dietro: escono
              dai suoi fianchi mentre si raccoglie */}
          <div className="carico-punto sx"><div className="carico-battito" /></div>
          <div className="carico-punto dx"><div className="carico-battito" /></div>
          {/* la palla, che diventa lei stessa il puntino di mezzo */}
          <div className="carico-respiro"><div className="carico-corpo" /></div>
          {/* la faccia in un svg suo: si anima la RADICE, che e' un riquadro
              HTML e passa dal compositore */}
          <svg viewBox="0 0 220 220" className="mascotte-arte carico-faccia">
            <FacciaMascotte />
          </svg>
        </div>
      </div>
    </div>
  )
}
